/**
 * Danmarks geografi til omsætningskortet på Marketing → Blended ([D-470]).
 *
 * Regioner, kommuner og opslaget postnummer → kommune er GENERERET af
 * web/scripts/build-dk-geo.mjs fra Geodatastyrelsens DAGI-grænser
 * (dk-geo.generated.ts). Her bor kun opslagene, så server og web tildeler et
 * postnummer til samme kommune.
 *
 * Et postnummer der krydser en kommunegrænse, tildeles den kommune der dækker
 * størst del af det (~). Erhvervs- og boks-postnumre uden eget område (fx 0800,
 * 0900) findes ikke i grænsedata og giver null: de står som "postnummer uden
 * for kortet", ikke gættet ind i en kommune. Et felt uden cifre ("X",
 * "AFVENTER", "LEVERING") er en pladsholder og tæller som intet postnummer.
 */
import { DK_KOMMUNER, DK_POSTNR_KOMMUNE, DK_POSTNR_RANGES, DK_REGIONS } from './dk-geo.generated.js';

export { DK_KOMMUNER, DK_POSTNR_SPLIT_COUNT, DK_REGIONS } from './dk-geo.generated.js';
export type { DkKommune, DkRegion } from './dk-geo.generated.js';

const KOMMUNE_BY_CODE = new Map(DK_KOMMUNER.map((k) => [k.code, k]));
const REGION_BY_CODE = new Map(DK_REGIONS.map((r) => [r.code, r]));

/** Normaliserer et dansk postnummer ('DK-2600', ' 2600 ', '1824 FREDERIKSBERG') til 4 cifre, ellers null. */
export function normalizeDkPostnr(raw: string | null | undefined): string | null {
  const m = /^(?:DK-?)?\s*(\d{4})(?:\s+\D.*)?$/i.exec(String(raw ?? '').trim());
  return m ? m[1]! : null;
}

/** Kommunekoden for et dansk postnummer, eller null når det ikke ligger på kortet. */
export function kommuneOfPostnr(raw: string | null | undefined): string | null {
  const pc = normalizeDkPostnr(raw);
  if (!pc) return null;
  const direct = DK_POSTNR_KOMMUNE[pc];
  if (direct) return direct;
  const n = Number(pc);
  for (const [from, to, code] of DK_POSTNR_RANGES) if (n >= from && n <= to) return code;
  return null;
}

export function dkKommuneName(code: string): string {
  return KOMMUNE_BY_CODE.get(code)?.name ?? code;
}

export function dkRegionOfKommune(code: string): string | null {
  return KOMMUNE_BY_CODE.get(code)?.region ?? null;
}

export function dkRegionName(code: string): string {
  return REGION_BY_CODE.get(code)?.name ?? code;
}

/**
 * Landekoden for et dokument: BC skriver DK, når feltet er tomt på et dansk
 * firma, så en tom kode tælles som Danmark. Ukendte koder vises som de står.
 */
export function normalizeCountryCode(raw: string | null | undefined): string {
  const c = String(raw ?? '').trim().toUpperCase();
  return c === '' ? 'DK' : c;
}

/** Danske landenavne for de koder vi møder i BC; resten vises som koden. */
const COUNTRY_NAMES: Record<string, string> = {
  DK: 'Danmark',
  SE: 'Sverige',
  NO: 'Norge',
  DE: 'Tyskland',
  FI: 'Finland',
  IS: 'Island',
  FO: 'Færøerne',
  GL: 'Grønland',
  NL: 'Holland',
  BE: 'Belgien',
  FR: 'Frankrig',
  GB: 'Storbritannien',
  UK: 'Storbritannien',
  IE: 'Irland',
  ES: 'Spanien',
  PT: 'Portugal',
  IT: 'Italien',
  CH: 'Schweiz',
  AT: 'Østrig',
  PL: 'Polen',
  CZ: 'Tjekkiet',
  LT: 'Litauen',
  LV: 'Letland',
  EE: 'Estland',
  US: 'USA',
};

export function countryNameDa(code: string): string {
  return COUNTRY_NAMES[code] ?? code;
}
